import { 
  Headphones, 
  Brain, 
  MessageSquare, 
  BarChart3, 
  Users, 
  Bot, 
  Monitor, 
  Award, 
  Smile, 
  GraduationCap,
  Phone,
  Globe,
  Shield,
  Zap,
  Activity,
  Settings,
  Laptop,
  TrendingUp,
  Target,
  LineChart
} from 'lucide-react';
import type { Product } from './types';

export const bpoContactCentreProducts: Product[] = [
  // 1. AI Conversation Intelligence
  {
    id: 'ai-conversation-intelligence',
    title: 'AI Conversation Intelligence',
    subtitle: 'Real-Time Speech Analytics & Customer Insight Platform',
    description: 'Transform every customer conversation into actionable intelligence with AI-powered speech analytics, real-time sentiment detection, and automated insight extraction for BPO and contact centre operations.',
    icon: Brain,
    image: 'https://images.unsplash.com/photo-1485827404703-89b55fcc595e?ixlib=rb-4.0.3&auto=format&fit=crop&w=1200&q=80',
    category: 'BPO & Contact Centre',
    status: 'Available',
    businessTypes: ['#bpo', '#contact-centre', '#call-centre', '#speech-analytics', '#ai-intelligence'],
    features: [
      'Real-Time Speech-to-Text Transcription',
      'Multi-Language Support (50+ Languages)',
      'AI Sentiment & Emotion Detection',
      'Customer Intent Recognition Engine',
      'Automated Topic Classification',
      'Competitive Mention Tracking',
      'Compliance Keyword Monitoring',
      'Agent Performance Insights',
      'Trend Analysis Dashboard',
      'Custom Alert Configuration'
    ],
    pricing: { setup: 48000, monthly: 2208 },
    color: 'from-purple-600 to-indigo-700'
  },

  // 2. AI Quality Automation
  {
    id: 'ai-quality-automation',
    title: 'AI Quality Automation',
    subtitle: 'Automated QA Scoring & Compliance Monitoring',
    description: 'Automate 100% of quality evaluations with AI-powered scoring, replacing manual sampling with comprehensive interaction analysis and intelligent compliance monitoring.',
    icon: Award,
    image: 'https://images.unsplash.com/photo-1460925895917-afdab827c52f?ixlib=rb-4.0.3&auto=format&fit=crop&w=1200&q=80',
    category: 'BPO & Contact Centre',
    status: 'Available',
    businessTypes: ['#bpo', '#quality-assurance', '#contact-centre', '#compliance', '#automation'],
    features: [
      'AI-Powered Auto-Scoring Engine',
      '100% Interaction Evaluation',
      'Custom Scorecard Builder',
      'Compliance Risk Detection',
      'Root Cause Analysis',
      'Calibration Management',
      'Coaching Recommendations',
      'Quality Trend Analytics',
      'Benchmark Comparisons',
      'Appeal Workflow Management'
    ],
    pricing: { setup: 52000, monthly: 2392 },
    color: 'from-emerald-600 to-teal-700'
  },

  // 3. AI Coaching Platform
  {
    id: 'ai-coaching-platform',
    title: 'AI Coaching Platform',
    subtitle: 'Intelligent Agent Development & Performance Coaching',
    description: 'Accelerate agent performance with AI-driven coaching recommendations, personalised learning paths, and real-time feedback loops that transform contact centre training.',
    icon: GraduationCap,
    image: 'https://images.unsplash.com/photo-1552664730-d307ca884978?ixlib=rb-4.0.3&auto=format&fit=crop&w=1200&q=80',
    category: 'BPO & Contact Centre',
    status: 'Available',
    businessTypes: ['#bpo', '#training', '#contact-centre', '#coaching', '#performance'],
    features: [
      'AI Coaching Recommendations',
      'Personalised Learning Paths',
      'Skill Gap Analysis',
      'Real-Time Performance Alerts',
      '1:1 Session Scheduling',
      'Goal Tracking & Progress',
      'Best Practice Library',
      'Peer Benchmarking',
      'Gamification Engine',
      'Manager Dashboard & Reports'
    ],
    pricing: { setup: 42000, monthly: 1932 },
    color: 'from-amber-600 to-orange-700'
  },

  // 4. ESAT & CSAT Analytics
  {
    id: 'esat-csat-analytics',
    title: 'ESAT & CSAT Analytics',
    subtitle: 'Employee & Customer Satisfaction Survey Platform',
    description: 'Comprehensive satisfaction measurement combining employee engagement (ESAT) and customer satisfaction (CSAT) analytics with AI-powered insights and automated survey distribution.',
    icon: Smile,
    image: 'https://images.unsplash.com/photo-1551836022-deb4988cc6c0?ixlib=rb-4.0.3&auto=format&fit=crop&w=1200&q=80',
    category: 'BPO & Contact Centre',
    status: 'Available',
    businessTypes: ['#bpo', '#surveys', '#contact-centre', '#customer-experience', '#employee-engagement'],
    features: [
      'Multi-Channel Survey Distribution',
      'Real-Time CSAT Collection',
      'Pulse Survey Automation',
      'NPS Tracking & Analysis',
      'AI Sentiment Analysis',
      'Driver Analysis Engine',
      'Benchmark Comparisons',
      'Custom Report Builder',
      'Action Plan Workflows',
      'Executive Dashboards'
    ],
    pricing: { setup: 35000, monthly: 1610 },
    color: 'from-pink-600 to-rose-700'
  },

  // 5. Omnichannel Contact Hub
  {
    id: 'omnichannel-contact-hub',
    title: 'Omnichannel Contact Hub',
    subtitle: 'Unified Voice, Email, Chat & Social Interaction Platform',
    description: 'Deliver seamless customer experiences across every channel with our unified omnichannel platform featuring intelligent routing, conversation continuity, and AI-powered agent assistance.',
    icon: Globe,
    image: 'https://images.unsplash.com/photo-1516321318423-f06f85e504b3?ixlib=rb-4.0.3&auto=format&fit=crop&w=1200&q=80',
    category: 'BPO & Contact Centre',
    status: 'Available',
    businessTypes: ['#bpo', '#omnichannel', '#contact-centre', '#customer-service', '#unified-comms'],
    features: [
      'Unified Agent Desktop',
      'Voice, Chat, Email & Social',
      'WhatsApp Business Integration',
      'Intelligent Routing Engine',
      'Conversation Continuity',
      'AI Suggested Responses',
      'Real-Time Translation',
      'Queue Management',
      'Channel Analytics',
      'SLA Monitoring'
    ],
    pricing: { setup: 65000, monthly: 2990 },
    color: 'from-blue-600 to-cyan-700'
  },

  // 6. Performance Dashboards
  {
    id: 'performance-dashboards',
    title: 'Performance Dashboards',
    subtitle: 'Real-Time Agent, Team & Project Analytics',
    description: 'Empower managers with real-time visibility into agent, team, and project performance through customisable dashboards, KPI tracking, and predictive analytics for BPO operations.',
    icon: BarChart3,
    image: 'https://images.unsplash.com/photo-1551288049-bebda4e38f71?ixlib=rb-4.0.3&auto=format&fit=crop&w=1200&q=80',
    category: 'BPO & Contact Centre',
    status: 'Available',
    businessTypes: ['#bpo', '#analytics', '#contact-centre', '#reporting', '#kpi-tracking'],
    features: [
      'Real-Time KPI Widgets',
      'Agent Performance Cards',
      'Team Comparison Views',
      'Project Health Metrics',
      'Custom Dashboard Builder',
      'Scheduled Report Delivery',
      'Drill-Down Analytics',
      'Predictive Trending',
      'Wallboard Display Mode',
      'Mobile-First Design'
    ],
    pricing: { setup: 38000, monthly: 1748 },
    color: 'from-violet-600 to-purple-700'
  },

  // 7. Predictive Analytics Suite
  {
    id: 'predictive-analytics-suite',
    title: 'Predictive Analytics Suite',
    subtitle: 'AI Forecasting & Business Intelligence Platform',
    description: 'Harness machine learning for accurate volume forecasting, churn prediction, and operational insights that drive strategic decision-making in contact centre operations.',
    icon: TrendingUp,
    image: 'https://images.unsplash.com/photo-1504868584819-f8e8b4b6d7e3?ixlib=rb-4.0.3&auto=format&fit=crop&w=1200&q=80',
    category: 'BPO & Contact Centre',
    status: 'Available',
    businessTypes: ['#bpo', '#forecasting', '#contact-centre', '#machine-learning', '#bi'],
    features: [
      'AI Volume Forecasting',
      'Churn Risk Prediction',
      'Attrition Modelling',
      'Revenue Impact Analysis',
      'Capacity Planning Tools',
      'What-If Scenario Builder',
      'Anomaly Detection',
      'Custom ML Models',
      'Data Integration Hub',
      'Executive Insights Portal'
    ],
    pricing: { setup: 58000, monthly: 2668 },
    color: 'from-indigo-600 to-blue-700'
  },

  // 8. Agent Mobile Portal
  {
    id: 'agent-mobile-portal',
    title: 'Agent Mobile Portal',
    subtitle: 'Mobile-First Agent Experience & Self-Service',
    description: 'Empower agents with a mobile-first experience for schedule viewing, shift swapping, performance tracking, and real-time communication—perfect for remote and hybrid BPO workforces.',
    icon: Laptop,
    image: 'https://images.unsplash.com/photo-1512941937669-90a1b58e7e9c?ixlib=rb-4.0.3&auto=format&fit=crop&w=1200&q=80',
    category: 'BPO & Contact Centre',
    status: 'Available',
    businessTypes: ['#bpo', '#mobile', '#contact-centre', '#agent-experience', '#self-service'],
    features: [
      'Mobile Schedule Access',
      'Shift Swap Marketplace',
      'Time-Off Requests',
      'Real-Time Earnings View',
      'Performance Scorecard',
      'Push Notifications',
      'Team Chat Integration',
      'Training Module Access',
      'News & Announcements',
      'Biometric Clock-In'
    ],
    pricing: { setup: 32000, monthly: 1472 },
    color: 'from-cyan-600 to-teal-700'
  },

  // 9. Manager Command Centre
  {
    id: 'manager-command-centre',
    title: 'Manager Command Centre',
    subtitle: 'Mobile Management Dashboard & Operations Control',
    description: 'Give managers complete operational control from anywhere with mobile dashboards, real-time alerts, approval workflows, and team management tools for BPO supervisors.',
    icon: Monitor,
    image: 'https://images.unsplash.com/photo-1559136555-9303baea8ebd?ixlib=rb-4.0.3&auto=format&fit=crop&w=1200&q=80',
    category: 'BPO & Contact Centre',
    status: 'Available',
    businessTypes: ['#bpo', '#management', '#contact-centre', '#operations', '#mobile-command'],
    features: [
      'Real-Time Team Overview',
      'Exception Approvals',
      'Schedule Override Controls',
      'Agent Status Monitoring',
      'Escalation Management',
      'Mobile Coaching Notes',
      'Performance Alerts',
      'Intraday Management',
      'Capacity Adjustments',
      'Cross-Team Visibility'
    ],
    pricing: { setup: 40000, monthly: 1840 },
    color: 'from-slate-600 to-gray-700'
  },

  // 10. Softphone Pro
  {
    id: 'softphone-pro',
    title: 'Softphone Pro',
    subtitle: 'Enterprise Cloud Softphone for Inbound & Outbound',
    description: 'Feature-rich cloud softphone with HD voice, WebRTC calling, CRM integration, and advanced call controls designed for high-volume BPO and contact centre environments.',
    icon: Phone,
    image: 'https://images.unsplash.com/photo-1596524430615-b46475ddff6e?ixlib=rb-4.0.3&auto=format&fit=crop&w=1200&q=80',
    category: 'BPO & Contact Centre',
    status: 'Available',
    businessTypes: ['#bpo', '#telephony', '#contact-centre', '#voip', '#cloud-calling'],
    features: [
      'HD Voice Quality',
      'WebRTC Browser-Based',
      'Click-to-Dial from CRM',
      'Auto-Dialler Integration',
      'Call Transfer & Conference',
      'Voicemail Management',
      'Call Recording Controls',
      'Presence & Status',
      'Contact Directory',
      'Mobile Companion App'
    ],
    pricing: { setup: 30000, monthly: 1380 },
    color: 'from-green-600 to-emerald-700'
  },

  // 11. Case Management System
  {
    id: 'case-management-system',
    title: 'Case Management System',
    subtitle: 'Enterprise Ticket & Case Tracking Platform',
    description: 'Streamline complex customer issues with intelligent case management featuring automated routing, SLA tracking, escalation workflows, and comprehensive audit trails for BPO operations.',
    icon: MessageSquare,
    image: 'https://images.unsplash.com/photo-1454165804606-c3d57bc86b40?ixlib=rb-4.0.3&auto=format&fit=crop&w=1200&q=80',
    category: 'BPO & Contact Centre',
    status: 'Available',
    businessTypes: ['#bpo', '#ticketing', '#contact-centre', '#case-management', '#workflow'],
    features: [
      'Smart Case Routing',
      'SLA Management Engine',
      'Multi-Level Escalation',
      'Customer 360° View',
      'Automated Workflows',
      'Knowledge Base Integration',
      'Email-to-Case Automation',
      'Case Merge & Split',
      'Audit Trail & History',
      'Resolution Analytics'
    ],
    pricing: { setup: 45000, monthly: 2070 },
    color: 'from-orange-600 to-red-700'
  },

  // 12. Real-Time BI Reporting
  {
    id: 'real-time-bi-reporting',
    title: 'Real-Time BI Reporting',
    subtitle: 'Live Business Intelligence & Custom Analytics',
    description: 'Transform contact centre data into strategic insights with real-time BI dashboards, ad-hoc reporting, and advanced visualisations for BPO performance management.',
    icon: LineChart,
    image: 'https://images.unsplash.com/photo-1551288049-bebda4e38f71?ixlib=rb-4.0.3&auto=format&fit=crop&w=1200&q=80',
    category: 'BPO & Contact Centre',
    status: 'Available',
    businessTypes: ['#bpo', '#bi', '#contact-centre', '#reporting', '#data-visualisation'],
    features: [
      'Real-Time Data Streaming',
      'Custom Report Builder',
      'Drag-Drop Visualisations',
      'Scheduled Report Delivery',
      'Data Warehouse Integration',
      'Multi-Source Data Blending',
      'Role-Based Access',
      'Export to Excel/PDF',
      'Embedded Analytics',
      'API Access for External BI'
    ],
    pricing: { setup: 42000, monthly: 1932 },
    color: 'from-fuchsia-600 to-pink-700'
  },

  // 13. Audit & Compliance Manager
  {
    id: 'audit-compliance-manager',
    title: 'Audit & Compliance Manager',
    subtitle: 'GDPR, PCI-DSS & ISO Compliance Automation',
    description: 'Ensure regulatory compliance with automated audit trails, consent management, data retention policies, and comprehensive compliance reporting for BPO operations.',
    icon: Shield,
    image: 'https://images.unsplash.com/photo-1563986768494-4dee2763ff3f?ixlib=rb-4.0.3&auto=format&fit=crop&w=1200&q=80',
    category: 'BPO & Contact Centre',
    status: 'Available',
    businessTypes: ['#bpo', '#compliance', '#contact-centre', '#gdpr', '#audit'],
    features: [
      'Automated Compliance Checks',
      'GDPR Consent Management',
      'PCI-DSS Controls',
      'Data Retention Automation',
      'Audit Trail Logging',
      'Risk Assessment Tools',
      'Compliance Dashboards',
      'Evidence Collection',
      'Policy Management',
      'Regulatory Reporting'
    ],
    pricing: { setup: 50000, monthly: 2300 },
    color: 'from-red-600 to-rose-700'
  },

  // 14. User & Access Management
  {
    id: 'user-access-management',
    title: 'User & Access Management',
    subtitle: 'Enterprise Identity & Permission Control',
    description: 'Centralised identity and access management with role-based permissions, SSO integration, and comprehensive user lifecycle management for BPO environments.',
    icon: Users,
    image: 'https://images.unsplash.com/photo-1522202176988-66273c2fd55f?ixlib=rb-4.0.3&auto=format&fit=crop&w=1200&q=80',
    category: 'BPO & Contact Centre',
    status: 'Available',
    businessTypes: ['#bpo', '#identity', '#contact-centre', '#access-control', '#security'],
    features: [
      'Role-Based Access Control',
      'SSO Integration (SAML/OIDC)',
      'Multi-Factor Authentication',
      'User Provisioning Automation',
      'Permission Templates',
      'Access Request Workflows',
      'Session Management',
      'Login Audit Logs',
      'Password Policy Enforcement',
      'Offboarding Automation'
    ],
    pricing: { setup: 35000, monthly: 1610 },
    color: 'from-sky-600 to-blue-700'
  },

  // 15. AI Chatbot Builder
  {
    id: 'ai-chatbot-builder',
    title: 'AI Chatbot Builder',
    subtitle: 'No-Code Conversational AI & Virtual Agent Platform',
    description: 'Deploy intelligent chatbots across channels with our no-code builder featuring NLU, intent recognition, and seamless handoff to human agents for BPO customer service.',
    icon: Bot,
    image: 'https://images.unsplash.com/photo-1531746790731-6c087fecd65a?ixlib=rb-4.0.3&auto=format&fit=crop&w=1200&q=80',
    category: 'BPO & Contact Centre',
    status: 'Available',
    businessTypes: ['#bpo', '#chatbot', '#contact-centre', '#virtual-agent', '#automation'],
    features: [
      'No-Code Bot Builder',
      'Pre-Built Industry Templates',
      'NLU Intent Recognition',
      'Multi-Language Support',
      'Omnichannel Deployment',
      'Human Handoff Logic',
      'Conversation Analytics',
      'A/B Testing Framework',
      'CRM Integration',
      'Continuous Learning'
    ],
    pricing: { setup: 55000, monthly: 2530 },
    color: 'from-violet-600 to-indigo-700'
  },

  // 16. Screen Recording System
  {
    id: 'screen-recording-system',
    title: 'Screen Recording System',
    subtitle: '100% Screen Capture & Desktop Analytics',
    description: 'Capture and analyse agent desktop activity with synchronised screen recording, productivity tracking, and process compliance monitoring for BPO quality assurance.',
    icon: Monitor,
    image: 'https://images.unsplash.com/photo-1527689368864-3a821dbccc34?ixlib=rb-4.0.3&auto=format&fit=crop&w=1200&q=80',
    category: 'BPO & Contact Centre',
    status: 'Available',
    businessTypes: ['#bpo', '#screen-recording', '#contact-centre', '#desktop-analytics', '#qa'],
    features: [
      '100% Screen Capture',
      'Call-Sync Recording',
      'Application Tracking',
      'Idle Time Detection',
      'PII Masking',
      'Secure Playback',
      'Search & Filter',
      'Annotation Tools',
      'Compliance Archiving',
      'Integration APIs'
    ],
    pricing: { setup: 38000, monthly: 1748 },
    color: 'from-gray-600 to-slate-700'
  },

  // 17. Live Agent Assist
  {
    id: 'live-agent-assist',
    title: 'Live Agent Assist',
    subtitle: 'AI-Powered Real-Time Agent Guidance',
    description: 'Provide agents with real-time AI suggestions, knowledge prompts, and compliance alerts during live customer interactions to boost first-call resolution and reduce handle time.',
    icon: Zap,
    image: 'https://images.unsplash.com/photo-1553877522-43269d4ea984?ixlib=rb-4.0.3&auto=format&fit=crop&w=1200&q=80',
    category: 'BPO & Contact Centre',
    status: 'Available',
    businessTypes: ['#bpo', '#ai-assist', '#contact-centre', '#real-time', '#agent-support'],
    features: [
      'Real-Time AI Suggestions',
      'Knowledge Base Prompts',
      'Script Compliance Alerts',
      'Sentiment Indicators',
      'Next-Best-Action Guidance',
      'Supervisor Whisper Mode',
      'Auto-Form Population',
      'Call Summary Generation',
      'Upsell/Cross-Sell Prompts',
      'Integration with CRM'
    ],
    pricing: { setup: 60000, monthly: 2760 },
    color: 'from-yellow-600 to-amber-700'
  },

  // 18. Knowledge Management System
  {
    id: 'knowledge-management-system',
    title: 'Knowledge Management System',
    subtitle: 'Centralised Knowledge Base & Content Hub',
    description: 'Centralise all organisational knowledge with AI-powered search, article versioning, and content governance for consistent customer service across all BPO channels.',
    icon: Settings,
    image: 'https://images.unsplash.com/photo-1481627834876-b7833e8f5570?ixlib=rb-4.0.3&auto=format&fit=crop&w=1200&q=80',
    category: 'BPO & Contact Centre',
    status: 'Available',
    businessTypes: ['#bpo', '#knowledge-base', '#contact-centre', '#content', '#self-service'],
    features: [
      'AI-Powered Search',
      'Article Versioning',
      'Content Governance',
      'Multi-Language Support',
      'Decision Trees',
      'Rich Media Support',
      'Analytics & Insights',
      'Agent Feedback Loop',
      'Self-Service Portal',
      'API & Widget Embedding'
    ],
    pricing: { setup: 40000, monthly: 1840 },
    color: 'from-teal-600 to-cyan-700'
  },

  // 19. Customer Effort Score Platform
  {
    id: 'customer-effort-score',
    title: 'Customer Effort Score Platform',
    subtitle: 'CES Measurement & Friction Reduction',
    description: 'Measure and reduce customer effort across all touchpoints with CES surveys, journey friction analysis, and actionable recommendations for BPO service improvement.',
    icon: Activity,
    image: 'https://images.unsplash.com/photo-1551288049-bebda4e38f71?ixlib=rb-4.0.3&auto=format&fit=crop&w=1200&q=80',
    category: 'BPO & Contact Centre',
    status: 'Available',
    businessTypes: ['#bpo', '#ces', '#contact-centre', '#customer-experience', '#friction-reduction'],
    features: [
      'CES Survey Automation',
      'Journey Friction Analysis',
      'Touchpoint Mapping',
      'Root Cause Identification',
      'Action Recommendations',
      'Trend Tracking',
      'Benchmark Comparisons',
      'Executive Reporting',
      'Integration with CX Stack',
      'Closed-Loop Feedback'
    ],
    pricing: { setup: 36000, monthly: 1656 },
    color: 'from-lime-600 to-green-700'
  },

  // 20. Workforce Gamification Platform
  {
    id: 'workforce-gamification',
    title: 'Workforce Gamification Platform',
    subtitle: 'Employee Engagement & Performance Motivation',
    description: 'Boost agent engagement and performance with gamified leaderboards, achievement badges, team challenges, and reward programmes designed for BPO and contact centre workforces.',
    icon: Target,
    image: 'https://images.unsplash.com/photo-1552664730-d307ca884978?ixlib=rb-4.0.3&auto=format&fit=crop&w=1200&q=80',
    category: 'BPO & Contact Centre',
    status: 'Available',
    businessTypes: ['#bpo', '#gamification', '#contact-centre', '#engagement', '#motivation'],
    features: [
      'Gamified Leaderboards',
      'Achievement Badges',
      'Team Challenges',
      'Points & Rewards System',
      'Real-Time Performance Feeds',
      'Manager Recognition Tools',
      'Custom Game Mechanics',
      'Mobile Engagement App',
      'Analytics & ROI Tracking',
      'Integration with WFM'
    ],
    pricing: { setup: 34000, monthly: 1564 },
    color: 'from-rose-600 to-pink-700'
  }
];
